// ChangePasswordView.jsx
import { useState } from 'react';
import styled from 'styled-components';
import { Input } from '../../../components/ui';
import { changePassword } from '../../../api/services/customerService';
import { useSelector } from 'react-redux';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Title = styled.h1`
  width: 100%;
  font-size: 24px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const InputGroup = styled.div`
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: #444;
`;

const InputContainer = styled.div`
  width: 100%;
  border: 1px solid #ccc;
  margin-bottom: 20px;
`;

const SubmitButton = styled.button`
  width: 80%;
  padding: 12px;
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  margin-top: 1rem;
  transition: background-color 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 18px 0;
  padding: 13px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  ${(props) =>
    props.type === 'success'
      ? 'background:#e9f7ef; color:#1e7e34; border:1px solid #bfe3cd;'
      : 'background:#fdecea; color:#c0392b; border:1px solid #f5c2c0;'}

  svg {
    flex-shrink: 0;
    font-size: 17px;
  }
`;

const ChangePasswordView = () => {
  const userInfo = useSelector((state) => state.userInfo);

  const [passwords, setPasswords] = useState({
    old: '',
    new: '',
    confirm: '',
  });

  // 화면 내 알림 (네이티브 alert 가 브라우저에서 차단돼도 항상 표시)
  const [statusMsg, setStatusMsg] = useState(null); // { type:'success'|'error', text }

  const notify = (type, text) => {
    setStatusMsg({ type, text });
  };

  const handleSubmit = async () => {
    setStatusMsg(null);

    // 입력 검증
    if (!passwords.old || !passwords.new || !passwords.confirm) {
      notify('error', 'Please fill in all password fields.');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      notify('error', 'New password and confirm password do not match.');
      return;
    }

    try {
      const result = await changePassword(
        userInfo.userId,
        userInfo.idCustomer,
        passwords.old,
        passwords.new,
      );
      if (result && result.is_success) {
        notify('success', 'Password changed successfully.');
        setPasswords({ old: '', new: '', confirm: '' });
      } else {
        notify(
          'error',
          (result && result.message) ||
            'Failed to change password. Please try again.',
        );
      }
    } catch (e) {
      console.error('Change password error:', e);
      notify('error', 'An error occurred while changing the password.');
    }
  };

  return (
    <FormContainer onSubmit={(e) => e.preventDefault()}>
      <Title>Change Password</Title>

      {statusMsg && (
        <Message type={statusMsg.type}>
          {statusMsg.type === 'success' ? (
            <FaCheckCircle />
          ) : (
            <FaExclamationCircle />
          )}
          {statusMsg.text}
        </Message>
      )}

      <InputGroup>
        <InputLabel>Old password:</InputLabel>
        <InputContainer>
          <Input
            type='password'
            value={passwords.old}
            onChange={(value) => {
              setPasswords({ ...passwords, old: value });
            }}
          />
        </InputContainer>
      </InputGroup>

      <InputGroup>
        <InputLabel>New password:</InputLabel>
        <InputContainer>
          <Input
            type='password'
            value={passwords.new}
            onChange={(value) => {
              setPasswords({ ...passwords, new: value });
            }}
          />
        </InputContainer>
      </InputGroup>

      <InputGroup>
        <InputLabel>Confirm password:</InputLabel>
        <InputContainer>
          <Input
            type='password'
            value={passwords.confirm}
            onChange={(value) => {
              setPasswords({ ...passwords, confirm: value });
            }}
          />
        </InputContainer>
      </InputGroup>

      <SubmitButton
        type='button'
        onClick={() => {
          handleSubmit();
        }}
      >
        SUBMIT
      </SubmitButton>
    </FormContainer>
  );
};

export default ChangePasswordView;
